import React from "react";

import { faVideo } from "@awesome.me/kit-4c83d50d0d/icons/classic/light";

import type { Event } from "~/calendar/CalendarPage";
import { isDesktop } from "~/lib/utils";
import { type CALL, CallIcon, callName, maybeOpenCompanionMode } from "~/stream/VideoCallButton";
import ContextMenu from "~/ui/contextMenu";

import styles from "./ConferenceLinksMenu.module.css";

export default function ConferenceLinksMenu({ event }: { event: Event }) {
	const onJoinClick = React.useCallback(
		(call: CALL) => (e: React.MouseEvent) => {
			const isModifierActive = ["Alt", "AltGraph", "Control", "Hyper", "Meta", "OS", "Shift", "Super"].some(key =>
				e.getModifierState(key),
			);

			if (!isDesktop || isModifierActive) return;

			e.preventDefault();

			const w = window.screen.availWidth / 2;
			const h = window.screen.availHeight / 2;

			const windowFeatures = `width=${w},height=${h}`;

			maybeOpenCompanionMode(event.guid, call);
			window.open(call.url, "_blank", windowFeatures);
		},
		[event.guid],
	);

	const renderConferenceMenuItem = React.useCallback(
		call => {
			return (
				<ContextMenu.ItemLink
					key={call.url}
					target="_blank"
					rel="noopener noreferrer"
					href={call.url}
					onClick={onJoinClick(call)}
					hideExternalIcon
					className={styles.conferenceItem}
				>
					<CallIcon provider={call.provider} />
					&ensp;Join {callName(call.provider)}
				</ContextMenu.ItemLink>
			);
		},
		[onJoinClick],
	);

	const renderConferenceCalls = React.useCallback(() => {
		if (!event.conferenceCalls || event.conferenceCalls.length === 0) {
			return null;
		}

		if (event.conferenceCalls.length === 1) {
			const call = event.conferenceCalls[0];
			return renderConferenceMenuItem(call);
		}

		return (
			<ContextMenu.Sub>
				<ContextMenu.SubTrigger icon={faVideo}>
					<span>Join Conference</span>
				</ContextMenu.SubTrigger>
				<ContextMenu.Portal>
					<ContextMenu.SubContent>
						{event.conferenceCalls.map(call => renderConferenceMenuItem(call))}
					</ContextMenu.SubContent>
				</ContextMenu.Portal>
			</ContextMenu.Sub>
		);
	}, [event.conferenceCalls, renderConferenceMenuItem]);

	return renderConferenceCalls();
}
